import React, { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { blue } from '@material-ui/core/colors';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormPersonalDetails from './FormPersonalDetails';
import FormLoanDetails from './FormLoanDetails';
import FormFinancialsDetails from './FormFinancialsDetails';
import Complete from './Complete';
import Welcome from './Welcome';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import LinearProgress from '@material-ui/core/LinearProgress';
import { yupResolver } from '@hookform/resolvers/yup';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { getLanguage, getCuCode, getToken, bindForm } from '../utils';
import useSubmitApplication from '../hooks/useSubmitApplication';
import ErrorMessage from './ui/ErrorMessage';
import { formDefaultValues, validationSchema } from '../FormConsts';
import jwt_decode from 'jwt-decode'

const useStyles = makeStyles((theme) => ({
    buttonProgress: {
        color: blue[500],
        position: 'absolute',
        top: '50%',
        left: '50%',
        marginTop: -12,
        marginLeft: -12
    }
}));

function getStepContent(step, data, msg, msgFr) {
    switch (step) {
        case 0:
            return <Welcome data={data} />;
        case 1:
            return <FormLoanDetails data={data} />;
        case 2:
            return <FormPersonalDetails data={data} />;
        case 3:
            return <FormFinancialsDetails data={data} />;
        case 4:
            return <Complete msg={msg} msgFr={msgFr} />;
        default:
            return 'Unknown step';
    }
}

export default function MultiStepForm() {
    const classes = useStyles();
    const [activeStep, setActiveStep] = useState(0);
    const { t, i18n } = useTranslation();
    const steps = t('steps', { returnObjects: true });
    const mobileScreen = useMediaQuery('(max-width:600px)');
    const btnNextRef = useRef(null);
    const CU_CODE = getCuCode();
    const url = `${process.env.REACT_APP_BACKEND_URL}/v2/weblos/${CU_CODE}/loan`;
    const [sumbit, isLoading] = useSubmitApplication(url);
    const [errorFetchParams, setErrorFetchParams] = useState(null);
    const [completedFormMsg, setCompletedFormMsg] = useState('');
    const [completedFormMsgFr, setCompletedFormMsgFr] = useState('');

    const onSubmit = async (data) => {
        if (isLoading) return;
        if (await sumbit(data)) setActiveStep(4);
    };

    const [data, setData] = useState([]);

    const currentValidationSchema = validationSchema(t)[activeStep];
    const methods = useForm({
        shouldUnregister: false,
        defaultValues: formDefaultValues,
        resolver: yupResolver(currentValidationSchema),
        mode: 'onChange'
    });
    const { handleSubmit, watch, reset, trigger, setValue } = methods;

    const handleNext = async () => {
        const isStepValid = await trigger();
        if (isStepValid) setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
        reset();
    };

    useEffect(() => {
        // Add person fetch
        const fetchMember = (settings) => {
            window.localStorage.clear();
            const token = getToken();
            if (token && settings.enableT24) {
                localStorage.setItem('access_token', token);
                try {
                    var member = jwt_decode(token);
                    if (member.languageCode === 'fr') i18n.changeLanguage('fr');
                    const requestOptions = {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            Authorization: 'Bearer ' + localStorage.getItem('access_token')
                        },
                        body: JSON.stringify({
                            containerNO: member.containerNO,
                            languageCode: member.containerNO,
                            institutionCode: CU_CODE
                        })
                    };
                    fetch(`${process.env.REACT_APP_BACKEND_URL}/cfg/fetch`, requestOptions)
                        .then(async (response) => {
                            const data = await response.json();
                            if (!response.ok) {
                                const error = (data && data.Message) || response.status;
                                return Promise.reject(error);
                            }
                            bindForm(setValue, data);
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                } catch (error) {
                    //Invalid token
                    console.log(error);
                }
            }
        };
        
        const fetchAppParams = async () => {
            const lang = getLanguage() || 'en';
            i18n.changeLanguage(lang);
            const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/v2/weblos/${CU_CODE}?lang=${lang}`);
                if (!response.ok) {
                    const errorMessage = await response.text();
                    const msg = `${response.statusText} - ${errorMessage}`;
                    throw new Error(msg);
                }
                const data = await response.json();
                const { branches } = data;
                if (branches?.length > 0) setValue('loan.isMultiBranch', true);
                console.log(data);
                i18n.addResourceBundle(lang, 'los', data, true);
                setData(data);
                setCompletedFormMsg(data.completedFormText);
                setCompletedFormMsgFr(data.completedFormTextFr);
                fetchMember(data);
        };

        fetchAppParams().catch((error) => {
            setErrorFetchParams(`Oops, somthing has gone wrong : ${error.message}`);
        });

        i18n.on('languageChanged', (lng) => {
            if (lng === 'fr') {
                fetch(`${process.env.REACT_APP_BACKEND_URL}/v2/weblos/${CU_CODE}?lang=fr`)
                    .then((res) => res.json())
                    .then((data) => {
                        i18n.addResourceBundle('fr', 'los', data, true);
                        setData(data);
                        setCompletedFormMsg(data.completedFormText);
                        setCompletedFormMsgFr(data.completedFormTextFr);
                    });
            }
        });
    }, []);

    if (errorFetchParams) return <ErrorMessage message={errorFetchParams} type="error" />;

    return data.length === 0 ? (
        <LinearProgress />
    ) : (
        <main className="max-w-4xl m-auto border-gray-100 lg:border-l lg:border-r lg:border-b lg:rounded-b-md">
            <div className="bg-gray-50 px-4 py-3 md:px-8 md:py-4">
                <Stepper activeStep={activeStep} style={{ backgroundColor: '#F9FAFB' }}>
                    {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>
                                    {!mobileScreen ? label : null}
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <div>
                    {activeStep === steps.length ? (
                        <>
                            <Complete msg={completedFormMsg} msgFr={completedFormMsgFr} />
                            <Button onClick={handleReset} className={classes.button}>
                                {t('btnReset')}
                            </Button>
                        </>
                    ) : (
                        <FormProvider {...methods}>
                            <form>
                                <div className="mt-4" style={{ minHeight: '60vh' }}>
                                    {getStepContent(activeStep, data, completedFormMsg, completedFormMsgFr)}
                                </div>
                                <div className="flex justify-between md:justify-center pt-20 pb-5">
                                    <Button disabled={activeStep === 0} onClick={handleBack}>
                                        {t('btnBack')}
                                    </Button>
                                    {activeStep === steps.length - 1 ? (
                                        <div className="relative">
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={handleSubmit(onSubmit)}
                                                disabled={isLoading}>
                                                {t('btnSubmit')}
                                            </Button>
                                            {isLoading && (
                                                <CircularProgress
                                                    size={24}
                                                    className={classes.buttonProgress}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            disabled={!!!watch('loan.type')}
                                            onClick={handleNext}
                                            ref={btnNextRef}>
                                            {t('btnNext')}
                                        </Button>
                                    )}
                                </div>

                                {/* <pre>{JSON.stringify(watch(), null, 2)}</pre> */}
                            </form>
                        </FormProvider>
                    )}
                </div>
            </div>
        </main>
    );
}
